import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HeroSection from './HeroSection';
import LocationCard from './LocationCard';
import PitchSection from './PitchSection';
import DesktopDownload from './DesktopDownload';
import StickyDownloadCta from './StickyDownloadCta';
import GameOverView from './GameOverView';
import { getMockGame, mockVariants } from '../../data/mockGames';
import { fetchGamePreview } from '../../api/gamePreview';
import { detectDevice } from '../../utils/deviceDetection';
import {
  formatHumanDate,
  formatFomo,
  getCapacity,
  isGameOver,
} from '../../utils/gameFormatters';
import './GamePreview.css';

const Skeleton = () => (
  <>
    <div className="gp-skel-hero" />
    <div className="gp-section">
      <div className="gp-skel-line gp-skel-line--medium" />
      <div className="gp-skel-line gp-skel-line--short" />
    </div>
    <div className="gp-section">
      <div className="gp-skel-line" />
      <div className="gp-skel-line gp-skel-line--medium" />
    </div>
  </>
);

const NotFound = ({ deepLink }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="gp-over">
        <div className="gp-over__emoji" aria-hidden>🤔</div>
        <h1 className="gp-over__title">{t('open.preview.notFound.title')}</h1>
        <p className="gp-over__sub">{t('open.preview.notFound.subtitle')}</p>
      </div>
      <StickyDownloadCta deepLink={deepLink} />
    </>
  );
};

const GamePreview = ({ gameId, mockKey, deepLink, webUrl }) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ status: 'loading', game: null });
  const [toast, setToast] = useState(null);
  const device = detectDevice();
  const isMobile = device === 'ios' || device === 'android';

  useEffect(() => {
    let cancelled = false;

    if (mockKey === 'loading') {
      setState({ status: 'loading', game: null });
      return;
    }
    if (mockKey === 'notfound') {
      const tid = setTimeout(
        () => !cancelled && setState({ status: 'notfound', game: null }),
        350,
      );
      return () => {
        cancelled = true;
        clearTimeout(tid);
      };
    }
    if (mockKey && mockVariants[mockKey]) {
      const game = getMockGame(mockKey);
      const tid = setTimeout(
        () =>
          !cancelled &&
          setState({ status: 'ready', game: { ...game, id: gameId || game.id } }),
        350,
      );
      return () => {
        cancelled = true;
        clearTimeout(tid);
      };
    }

    if (!gameId) {
      setState({ status: 'notfound', game: null });
      return;
    }

    setState({ status: 'loading', game: null });
    fetchGamePreview(gameId, i18n.language)
      .then((game) => {
        if (cancelled) return;
        if (!game) {
          setState({ status: 'notfound', game: null });
          return;
        }
        setState({ status: 'ready', game });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'notfound', game: null });
      });

    return () => {
      cancelled = true;
    };
  }, [gameId, mockKey, i18n.language]);

  const handleShare = async () => {
    if (!state.game) return;
    const game = state.game;
    const headline = formatHumanDate(game.startDate, i18n.language, t);
    const fomo = formatFomo(game, t);
    const cap = getCapacity(game);
    void cap;
    const lines = [
      t('open.preview.share.heading', { sport: game.sportType.name }),
      `📅 ${headline}`,
      `📍 ${game.venue.name}`,
      `👥 ${fomo.label}`,
      '',
      t('open.preview.share.tagline'),
      window.location.href,
    ];
    const text = lines.join('\n');
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('open.preview.share.heading', { sport: game.sportType.name }),
          text,
          url: window.location.href,
        });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setToast(t('open.preview.cta.linkCopied'));
      setTimeout(() => setToast(null), 1800);
    } catch (e) {
      // ignore
    }
  };

  if (state.status === 'loading') {
    return (
      <div className="gp-page">
        <div className="gp-shell">
          <Skeleton />
        </div>
      </div>
    );
  }

  if (state.status === 'notfound' || !state.game) {
    return (
      <div className="gp-page">
        <div className="gp-shell">
          <NotFound deepLink={deepLink} />
        </div>
      </div>
    );
  }

  const { game } = state;

  if (isGameOver(game.status)) {
    return (
      <div className="gp-page">
        <div className="gp-shell">
          <GameOverView status={game.status} deepLink={deepLink} />
        </div>
      </div>
    );
  }

  return (
    <div className="gp-page">
      <div className="gp-shell">
        <HeroSection game={game} onShare={handleShare} toast={toast} />
        <LocationCard venue={game.venue} />
        <PitchSection />
        {!isMobile && (
          <div id="gp-desktop-download">
            <DesktopDownload />
          </div>
        )}
        <StickyDownloadCta deepLink={deepLink} mode={game.mode} webUrl={webUrl} />
      </div>
    </div>
  );
};

export default GamePreview;
